// The way a list fills: type a name, press Enter, type the next. The box keeps the focus
// and empties itself, so twenty names are twenty Enters and nothing else. Several lines
// pasted at once (from a spreadsheet, a message, another list) become several rows, their
// tab-separated cells filling the list's columns in order (listView.parsePaste).
//
// `preset` (listGroups.presetFor): while one group is shown, every row added lands in it —
// its state, or its value in the grouping column — and the box names the group, so a row
// typed into a filtered list never vanishes from it.
import { useRef, useState } from 'react';
import he from '../../i18n/he';
import Button from '../Button';
import { parsePaste, isMultiPaste } from './listView';

// `autoFocus` when the list opens empty: the first thing to do is type the first name.
export default function ListAddBox({ onAdd, autoFocus, columns = [], preset = null }) {
  const [name, setName] = useState('');
  const ref = useRef(null);
  const into = (row) => (preset
    ? { ...row, ...preset.row, cells: { ...(row.cells || {}), ...(preset.row.cells || {}) } }
    : row);

  const add = () => {
    const value = name.trim();
    if (!value) return;
    setName('');
    onAdd([into({ name: value })]);
    if (ref.current) ref.current.focus();
  };

  return (
    <div className="list-add">
      <svg className="list-add-plus" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M12 5v14M5 12h14" />
      </svg>
      <input
        ref={ref}
        autoFocus={autoFocus}
        value={name}
        maxLength={160}
        placeholder={he.lists.addName}
        aria-label={he.lists.addName}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        onPaste={(e) => {
          const text = e.clipboardData.getData('text');
          if (!isMultiPaste(text)) return;
          e.preventDefault();
          const rows = parsePaste(text, columns);
          if (rows.length) onAdd(rows.map(into));
        }}
      />
      {preset && <span className="list-add-into">{preset.label}</span>}
      <Button small onClick={add} disabled={!name.trim()}>{he.lists.addButton}</Button>
    </div>
  );
}
